import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { describeError } from "../../api/client";
import type { AdminCampaignSummary, AdminOverview, OperationalMetric } from "../../api/types";
import { RequireRole } from "../../auth/RequireRole";
import { hasRequiredRole } from "../../auth/roles";
import { useAccountRoles } from "../../auth/useAccountRoles";
import { Combobox } from "../../components/Combobox";
import { DeliveryProgressBar } from "../../components/DeliveryProgressBar";
import { EmptyState } from "../../components/EmptyState";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Pagination } from "../../components/Pagination";
import { SearchField } from "../../components/SearchField";
import { SegmentedControl } from "../../components/SegmentedControl";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../formatting/date";
import { formatCount, isValidCount } from "../../formatting/number";
import { isTerminalLifecycleState, resolveLifecycleStatus } from "../../formatting/status";
import { getOverview } from "../overview/api";
import { buildAllowedSearchParams } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { listCampaigns } from "./api";
import { CampaignCreatePanel } from "./CampaignCreatePanel";
import { CAMPAIGN_LIST_ALLOWED_KEYS, normalizeCampaignListSearchParams } from "./filters";
import { sortCampaigns, type SortState } from "./campaignSort";

function findMetric(metrics: readonly OperationalMetric[], key: string): OperationalMetric | undefined {
  return metrics.find((metric) => metric.key === key);
}

// User follow-up 2026-09-25: per-row delivery progress must reflect reality
// without a manual refresh — same polling convention already shipped for
// `CampaignMonitoringTab`. Shared by the list query and the overview query
// below so both poll (and stop polling) on the exact same cadence/condition.
const CAMPAIGN_LIST_POLL_INTERVAL_MS = 10_000;

/**
 * Codex finding (PR #383 round 5): `Array.prototype.every` on an empty array
 * is vacuously `true`, so an `enviando` (or any other non-`borrador`) tab
 * with initially zero rows made this report "settled" and stop polling
 * before ever fetching again — but a scheduled campaign can transition INTO
 * that filter later, and the tab would never notice. An empty page is only
 * genuine evidence of "nothing left to poll for" when the current filter is
 * one nothing transitions into automatically: `borrador` (only an explicit
 * operator action creates a new draft — the same exclusion
 * `allOverviewCampaignsTerminal` below already applies for the account-wide
 * poll).
 *
 * Returns a bound `isTerminal` closure instead of taking `estadoFilter`
 * alongside `page`, since `useQuery`'s `isTerminal` option is only ever
 * called with the query's own data.
 */
function allCampaignsTerminal(
  estadoFilter: string | undefined,
): (page: { items: readonly AdminCampaignSummary[] } | undefined) => boolean {
  return (page) => {
    if (!page) return false;
    if (page.items.length === 0) {
      return estadoFilter === "borrador";
    }
    return page.items.every((item) => isTerminalLifecycleState(item.estado));
  };
}

/**
 * Codex findings review (panel-redesign-v3-04, #2): the overview query's own
 * `isTerminal` used to read the LIST query's data (`allCampaignsTerminal(data)`
 * from the same closure) instead of its own response. That meant viewing a
 * tab whose CURRENT page happens to be all-terminal (e.g. "Completadas")
 * stopped the account-wide overview poll too, even while OTHER campaigns
 * (e.g. `enviando`/`programada` on a different tab) were still active and
 * should keep updating the header subtitle/tab counters. Account-wide
 * counters must settle on the account-wide response, never on whichever
 * page/tab happens to be open.
 */
// native review finding R3-003 (panel-redesign-v3-04, SUGGESTION): unlike
// `programada`/`enviando`/`parcial`, a `borrador` never changes state on its
// own -- only an explicit operator action (scheduling it) moves it. Counting
// it as "still active" here would keep this account-wide poll running every
// 10s indefinitely for an account that, in practice, is fully idle except
// for sitting drafts. It is excluded from this decision on purpose; this is
// NOT the same question as `isTerminalLifecycleState` answers elsewhere
// (e.g. row-level polling/rendering), where `borrador` still correctly
// counts as non-terminal.
// bounded-writer fix (panel-redesign-v3-04, item b): this used to be its OWN
// hand-maintained allow-list (`new Set(["programada", "enviando", "parcial"])`)
// -- any state NOT in that literal, including a future non-terminal one this
// list never learned about, silently counted as "doesn't matter" and could
// stop the poll regardless of its count. Re-derived as a deny-list from the
// canonical `isTerminalLifecycleState` instead (deliberately excluding
// `borrador` on purpose, per R3-003 above): an item only stops blocking the
// poll once it is EITHER empty, terminal, or the deliberately-idle
// `borrador` -- any other state (present or future) with a nonzero count
// keeps polling by default, the same "unknown stays non-terminal" direction
// `isTerminalLifecycleState`/`resolveLifecycleStatus` already take elsewhere.
function allOverviewCampaignsTerminal(overview: AdminOverview | undefined): boolean {
  if (!overview) return false;
  return overview.campaignStates.every(
    (item) => item.count === 0 || item.state === "borrador" || isTerminalLifecycleState(item.state),
  );
}

/**
 * REDISENO-PANEL.md §5.1 "Pestañas segmentadas con contador" — the 8 tabs,
 * in this exact order. `estado: undefined` is "Todas" (no `?estado=`, sums
 * every state's count).
 */
/** Round 3: settle time of the campaign-name search before it reaches the server. */
const SEARCH_DEBOUNCE_MS = 300;
/** Mirrors the backend cap (`AdminCampaignSqlBuilder.MaxSearchLength`). */
const MAX_SEARCH_LENGTH = 100;

const CAMPAIGN_TABS: readonly { estado?: string; label: string }[] = [
  { estado: undefined, label: "Todas" },
  { estado: "borrador", label: "Borradores" },
  { estado: "programada", label: "Programadas" },
  { estado: "enviando", label: "Enviando" },
  { estado: "completada", label: "Completadas" },
  { estado: "parcial", label: "Con errores" },
  { estado: "fallida", label: "Fallidas" },
  { estado: "cancelada", label: "Canceladas" },
];

type SortChoice = "fecha" | "nombre" | "estado";

const SORT_OPTIONS = [
  { value: "fecha", label: "Fecha de envío" },
  { value: "nombre", label: "Nombre" },
  { value: "estado", label: "Estado" },
];

/**
 * §5.1 "Ordenar por": "Fecha de envío (por defecto, descendente)" is the
 * backend's own already-sorted order (`fecha_programada DESC`, see
 * `campaignSort.ts`'s doc comment) — `INITIAL_SORT` (no active column) reuses
 * that order rather than re-sorting to the same effect.
 */
function sortStateFor(choice: SortChoice): SortState {
  if (choice === "nombre") return { column: "nombre", direction: "asc" };
  if (choice === "estado") return { column: "estado", direction: "asc" };
  return { column: null, direction: null };
}

/** Outline chevron, same icon vocabulary as the rest of the shell (round caps/joins, no fill, `aria-hidden`). */
function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="v2-campaigns-list__row-chevron"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/**
 * §5.1 row monitoring read: "X de Y enviados" / "Aún sin envíos" / "No se
 * envió" (cancelada), plus the figures the 6px `DeliveryProgressBar` needs.
 * progress-bar-sent: X is `enviados` (recipients ACS accepted — the sending
 * pace), not delivery confirmations. A missing `encolado`/`error` metric
 * degrades the whole cell to "—"; a missing `enviados` (older API) renders
 * "— de Y enviados" and hides the bar from assistive tech — never a
 * fabricated 0 (§9.2).
 */
function resolveMonitoringRow(
  estado: string,
  metrics: readonly OperationalMetric[],
  enviados: number | undefined,
): { head: string; sent: number; sentAvailable: boolean; errorCount: number; total: number } | null {
  const value = (key: string): number | null => {
    const metric = findMetric(metrics, key);
    return metric && metric.availability === "available" && typeof metric.value === "number" && Number.isFinite(metric.value)
      ? metric.value
      : null;
  };
  const encolado = value("encolado");
  const error = value("error");
  if (encolado === null || error === null) {
    return null;
  }
  const sentValue = isValidCount(enviados) ? enviados : null;
  const head =
    encolado === 0
      ? estado === "cancelada"
        ? "No se envió"
        : "Aún sin envíos"
      : `${sentValue === null ? "—" : formatCount(sentValue)} de ${formatCount(encolado)} enviados`;
  return { head, sent: sentValue ?? 0, sentAvailable: sentValue !== null, errorCount: error, total: encolado };
}

function metaFor(campaign: AdminCampaignSummary): string {
  // issue #330 (⚠ §9.5): `showName` stays null until the backend ships the
  // list-endpoint Show join — degrade to the template name alone rather than
  // rendering "Plantilla · " with a dangling separator.
  return campaign.showName ? `${campaign.templateName} · ${campaign.showName}` : campaign.templateName;
}

// Codex findings review (panel-redesign-v3-04, #3): a `cancelada` campaign
// that never actually sent anything -- labeling its date "Enviada" would
// claim the opposite, contradicting `resolveMonitoringRow`'s own "No se
// envió" text for the same row. It reads "Envío previsto" like borrador/
// programada instead.
//
// native review finding R3-004 (panel-redesign-v3-04, SUGGESTION): a
// `cancelada` cancelled MID-SEND (encolado > 0) DID partially send --
// `resolveMonitoringRow` already reads "X de Y entregados" for it. Always
// reading "Envío previsto" regardless contradicted THAT instead, implying
// nothing had gone out yet. `hasPartialSend` (true once anything was ever
// enqueued) picks between the two "No se envió"/"X de Y entregados" cases
// the same way `resolveMonitoringRow` itself does.
// Codex finding (PR #383 round 5): `CampaignDispatchService` explicitly
// closes a campaign `fallida` with zero enqueued messages when every
// recipient fails before enqueue (`CampaignDispatchService.cs:403-404`) --
// the exact same "nothing ever went out" shape `cancelada` already
// distinguishes via `hasPartialSend` above. Labeling it "Enviada"
// unconditionally presented a planned time as an actual send and
// contradicted the SAME row's own "Aún sin envíos" monitoring text.
function dateLabelFor(estado: string, hasPartialSend: boolean): string {
  if (estado === "borrador" || estado === "programada") {
    return "Envío previsto";
  }
  if (estado === "cancelada" || estado === "fallida") {
    return hasPartialSend ? "Enviada" : "Envío previsto";
  }
  return "Enviada";
}

/**
 * `/campanas` — REDISENO-PANEL.md §5.1 list: header with the account-wide
 * "N campañas · M enviando ahora" subtitle (from `overview.campaignStates`,
 * never the current page's own count), 8 segmented tabs with counters 1:1
 * with `?estado=`, an "Ordenar por" select over the current page
 * (`sortCampaigns`), and a single row-list (no `<table>`, no desktop/mobile
 * duplication — the same markup wraps at narrow widths). §1.1: batch UI
 * (grouping, "Programar todo el lote", the batch `ConfirmDialog`) is gone —
 * every campaign is always its own row now; `scheduleCampaignBatch` and its
 * types stay in `./api.ts`/`api/types.ts`, just unused here.
 */
export function CampaignListScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  // native review finding: the empty-state "Crear campaña" action was not
  // gated by role like the header's "+ Nueva campaña" trigger (wrapped in
  // `<RequireRole role="Operador">`). `EmptyState` renders its action button
  // internally from `actionLabel`/`onAction`, so it can't be wrapped in
  // `<RequireRole>` directly — same `hasRequiredRole` check `RequireRole`
  // itself uses, fail-closed (`useAccountRoles` starts at `[]`) until the
  // role resolves.
  const accountRoles = useAccountRoles();
  const canCreateCampaign = hasRequiredRole(accountRoles, "Operador");
  const [creationMode, setCreationMode] = useState<"none" | "open">("none");
  // `submitting` disables the header's own "+ Nueva campaña" trigger while a
  // create request is in flight (`createCampaignWithoutSegment`/
  // `saveAudienceFilter` accept no `AbortSignal` — same hard-block
  // convention the removed batch panel used).
  const [submitting, setSubmitting] = useState(false);
  const [sortChoice, setSortChoice] = useState<SortChoice>("fecha");
  // Round 3: name search. `q` is the raw input; `activeQ` is its trimmed, debounced value
  // (what reaches the server). Kept in component state, like the Contactos search.
  const [q, setQ] = useState("");
  const [activeQ, setActiveQ] = useState("");

  const normalization = useMemo(() => normalizeCampaignListSearchParams(searchParams), [searchParams]);
  const { params, normalized, changed } = normalization;

  useEffect(() => {
    if (changed) {
      setSearchParams(normalized, { replace: true });
    }
    // Only the raw searchParams identity should retrigger this — `normalized`/
    // `changed` are pure derivations of it computed above every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, changed]);

  const queryKey = useMemo(
    () => ["campaigns", params.estado ?? null, params.page, params.size, activeQ || null],
    [params.estado, params.page, params.size, activeQ],
  );

  const { data, error, loading, stale, updatedAt, refetch } = useQuery({
    key: queryKey,
    queryFn: () => listCampaigns({ estado: params.estado, q: activeQ || undefined, page: params.page, size: params.size }),
    pollIntervalMs: CAMPAIGN_LIST_POLL_INTERVAL_MS,
    isTerminal: allCampaignsTerminal(params.estado),
  });

  // §5.1: the header subtitle and every tab's counter come from the
  // account-wide `overview.campaignStates` — never the current page's own
  // (filtered, paginated) `data.total` — same shared query key
  // `OverviewScreen`/`AppShell` already populate (deduped, no new fetch).
  //
  // Codex findings review (#6): this never polled, so a row's estado
  // changing via the list query's own 10s poll (below) never moved the
  // header subtitle/tab counters reporting on it, even while the list stayed
  // open and visibly refreshing. Polled on the SAME cadence.
  //
  // Codex findings review (#2): polling must stop on the OVERVIEW's own
  // terminality (`allOverviewCampaignsTerminal`, reading this query's own
  // response), never the list's — the list's `data` is scoped to the
  // currently filtered/paginated tab, so it says nothing about whether OTHER
  // campaigns elsewhere are still active.
  const {
    data: overview,
    error: overviewError,
    loading: overviewLoading,
    refreshing: overviewRefreshing,
    refetch: refetchOverview,
  } = useQuery({
    key: ["overview"],
    queryFn: getOverview,
    pollIntervalMs: CAMPAIGN_LIST_POLL_INTERVAL_MS,
    isTerminal: allOverviewCampaignsTerminal,
  });

  const countFor = useCallback(
    (estado?: string): number => {
      if (!overview) return 0;
      if (!estado) {
        return overview.campaignStates.reduce((sum, item) => sum + item.count, 0);
      }
      return overview.campaignStates.find((item) => item.state === estado)?.count ?? 0;
    },
    [overview],
  );

  const totalCampaigns = countFor(undefined);
  const sendingNow = countFor("enviando");

  // native review finding: while `overview` is still loading or has failed,
  // `countFor` returns a fabricated `0` for every state — showing "Borradores
  // 0" etc. looks like a confirmed empty account rather than "we don't know
  // yet". Omit the number entirely until real `overview` data exists, same
  // rule the header subtitle above already follows (`{overview && ...}`).
  const tabOptions = useMemo(
    () =>
      CAMPAIGN_TABS.map((tab) => ({
        value: tab.estado ?? "",
        label: overview ? `${tab.label} ${countFor(tab.estado)}` : tab.label,
      })),
    [overview, countFor],
  );

  const currentTabLabel = useMemo(
    () => CAMPAIGN_TABS.find((tab) => (tab.estado ?? "") === (params.estado ?? ""))?.label ?? "Todas",
    [params.estado],
  );

  const sortedCampaigns = useMemo(
    () => (data ? sortCampaigns(data.items, sortStateFor(sortChoice)) : []),
    [data, sortChoice],
  );

  // Only the list's OWN allow-listed keys travel onto the detail link
  // (never an arbitrary `returnTo`).
  const contextSuffix = useMemo(() => {
    const allowed = buildAllowedSearchParams(searchParams.toString(), CAMPAIGN_LIST_ALLOWED_KEYS);
    const asString = allowed.toString();
    return asString ? `?${asString}` : "";
  }, [searchParams]);

  const updateParams = useCallback(
    (next: { estado?: string; page: number; size: number }) => {
      const search = new URLSearchParams();
      if (next.estado) {
        search.set("estado", next.estado);
      }
      search.set("page", String(next.page));
      search.set("size", String(next.size));
      setSearchParams(search);
    },
    [setSearchParams],
  );

  // No "Buscar" button: settle the typed value, then go back to page 1 (the previous page
  // number can't exist in a narrower result set) keeping the estado tab and page size.
  const latestParams = useRef(params);
  latestParams.current = params;
  const appliedQ = useRef("");
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const next = q.trim();
      if (next === appliedQ.current) return;
      appliedQ.current = next;
      setActiveQ(next);
      updateParams({ estado: latestParams.current.estado, page: 1, size: latestParams.current.size });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [q, updateParams]);

  return (
    <section aria-label="Campañas" className="v2-campaigns-list">
      <header className="v2-campaigns-list__header">
        <div className="v2-campaigns-list__header-titles">
          <h1>Campañas</h1>
          {overview && (
            <span className="v2-campaigns-list__header-subtitle">
              {totalCampaigns} {totalCampaigns === 1 ? "campaña" : "campañas"} · {sendingNow} enviando ahora
            </span>
          )}
        </div>
        <RequireRole role="Operador">
          <button
            type="button"
            className="v2-campaigns-list__create-trigger v2-u-target-min v2-btn-primary"
            onClick={() => setCreationMode("open")}
            disabled={submitting}
          >
            + Nueva campaña
          </button>
        </RequireRole>
      </header>

      {creationMode === "open" && (
        <CampaignCreatePanel
          onClose={() => setCreationMode("none")}
          onCreated={() => {
            setCreationMode("none");
            refetch();
          }}
          onSubmittingChange={setSubmitting}
        />
      )}

      {overviewError !== undefined && (
        // Non-intrusive (role="status", via FeedbackPanel's "stale" variant):
        // the tab counters/subtitle are secondary context, not the page's own
        // data — a failed overview fetch must never interrupt (role="alert")
        // the campaign list itself, which can still load and work fine.
        <FeedbackPanel
          variant="stale"
          message={describeError(overviewError, "No se pudieron cargar los contadores de campañas.")}
          onRetry={refetchOverview}
          // Codex round 6 (PR #383): `overviewError` stays set until the
          // retry settles, so without this every extra click force-aborts
          // and replaces the in-flight request.
          disabled={overviewLoading || overviewRefreshing}
        />
      )}

      <div className="v2-campaigns-list__toolbar">
        <SearchField
          id="campaigns-q"
          label="Buscar campañas por nombre"
          value={q}
          onChange={setQ}
          maxLength={MAX_SEARCH_LENGTH}
        />
        <SegmentedControl
          ariaLabel="Filtrar campañas por estado"
          options={tabOptions}
          value={params.estado ?? ""}
          onChange={(value) => updateParams({ estado: value || undefined, page: 1, size: params.size })}
        />
        <Field label="Ordenar por" htmlFor="campaigns-sort">
          <Combobox
            id="campaigns-sort"
            searchable={false}
            value={sortChoice}
            onChange={(next) => setSortChoice(next as SortChoice)}
            options={SORT_OPTIONS}
          />
        </Field>
      </div>

      {loading && <p role="status">Cargando campañas…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar el listado de campañas.")}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          {stale && (
            <FeedbackPanel
              variant="stale"
              message={`Datos anteriores${updatedAt ? ` · ${formatDateTime(new Date(updatedAt).toISOString())}` : ""}`}
              onRetry={refetch}
            />
          )}

          {sortedCampaigns.length === 0 ? (
            <EmptyState
              title={
                activeQ
                  ? `No hay campañas que coincidan con «${activeQ}».`
                  : `No hay campañas en "${currentTabLabel}"`
              }
              // Codex finding (PR #383 round 5): `canCreateCampaign` already
              // removes the action button for a Consulta-only user, but this
              // description unconditionally told them to create a new
              // campaign — advertising a remedy with no control that can
              // perform it. Role-aware copy omits it when creation isn't
              // permitted.
              description={
                activeQ
                  ? "Prueba con otro nombre o limpia la búsqueda."
                  : canCreateCampaign
                  ? "Prueba con otra pestaña o crea una campaña nueva."
                  : "Prueba con otra pestaña."
              }
              actionLabel={canCreateCampaign ? "Crear campaña" : undefined}
              onAction={canCreateCampaign ? () => setCreationMode("open") : undefined}
            />
          ) : (
            <>
              <ul aria-label="Campañas" className="v2-campaigns-list__rows">
                {sortedCampaigns.map((campaign) => {
                  const { label, tone } = resolveLifecycleStatus(campaign.estado);
                  const monitoring = resolveMonitoringRow(campaign.estado, campaign.monitoring, campaign.enviados);
                  return (
                    <li key={campaign.campaignId} className="v2-campaigns-list__row-item">
                      <Link to={`/campanas/${campaign.campaignId}${contextSuffix}`} className="v2-campaigns-list__row">
                        <span className="v2-campaigns-list__row-main">
                          <span className="v2-campaigns-list__row-title">
                            <span className="v2-campaigns-list__row-name">{campaign.nombre}</span>
                            <StatusPill label={label} tone={tone} />
                          </span>
                          <span className="v2-campaigns-list__row-meta">{metaFor(campaign)}</span>
                        </span>
                        <span className="v2-campaigns-list__row-date">
                          <span className="v2-campaigns-list__row-date-label">
                            {dateLabelFor(campaign.estado, monitoring !== null && monitoring.total > 0)}
                          </span>
                          <span className="v2-campaigns-list__row-date-value v2-u-nowrap">
                            {formatDateTime(campaign.fechaProgramada)}
                          </span>
                        </span>
                        <span className="v2-campaigns-list__row-monitoring">
                          {monitoring ? (
                            <>
                              <span className="v2-campaigns-list__row-monitoring-head">{monitoring.head}</span>
                              <DeliveryProgressBar
                                delivered={monitoring.sent}
                                errorCount={monitoring.errorCount}
                                total={monitoring.total}
                                height={6}
                                deliveredAvailable={monitoring.sentAvailable}
                                countLabel="enviados"
                                active={campaign.estado === "enviando"}
                              />
                              {monitoring.errorCount > 0 && (
                                <span className="v2-campaigns-list__row-errors">
                                  {monitoring.errorCount === 1 ? "1 error" : `${formatCount(monitoring.errorCount)} errores`}
                                </span>
                              )}
                            </>
                          ) : (
                            <span>—</span>
                          )}
                        </span>
                        <ChevronIcon />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <Pagination
                page={data.page}
                size={data.size}
                total={data.total}
                onPageChange={(nextPage) => updateParams({ estado: params.estado, page: nextPage, size: data.size })}
                onSizeChange={(nextSize) => updateParams({ estado: params.estado, page: 1, size: nextSize })}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
